import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { MouseEventHandler } from 'react';
import ImageWithFallback from '../../common/ImageWithFallback';
import type FeaturedHotel from '../../../../types/FeaturedHotel';
import { cardTestIds as testIds } from './testIds';
import getTargetCampaignAndCampaignsForTags from '../../../core/helpers/getTargetCampaignAndCampaignsForTags';
import displayInYen from '../../../core/displayInYen';
import { StartPrices } from '../../../../types/StartPrices';
import Lottie from '../../common/Lottie/Lottie';
import Loading from '../../../../public/animations/loading.json';
import { useSelector } from '../../../core/redux/store';
import HotelRating from '../../hotel/HotelRating/HotelRating';

type Props = {
  url: string;
  roomPhotoUrl: NonNullable<FeaturedHotel['image']>['url'];
  roomPhotoAltText: NonNullable<FeaturedHotel['image']>['alt'];
  startPrices?: StartPrices;
  isStartPriceLoading: boolean;
  webCampaigns: NonNullable<FeaturedHotel['webCampaigns']>;
  name: FeaturedHotel['name'];
  address: FeaturedHotel['address'];
  accessSimple: FeaturedHotel['accessSimple'];
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  prefetchImage?: boolean;
  hotelRating?: FeaturedHotel['hotelRating'];
};

const Card = ({
  roomPhotoUrl,
  roomPhotoAltText,
  url,
  startPrices,
  isStartPriceLoading,
  webCampaigns,
  name,
  address,
  accessSimple,
  onClick,
  prefetchImage,
  hotelRating,
}: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { userAttributes } = useSelector((x) => x.account);
  const isLoggedIn = Boolean(userAttributes?.accountId);
  const { campaignsForTags } = getTargetCampaignAndCampaignsForTags(webCampaigns, isLoggedIn);
  const startPrice = startPrices?.startPrice;
  const discountedStartPrice =
    isLoggedIn && startPrices?.membersDiscountedStartPrice
      ? startPrices?.membersDiscountedStartPrice
      : startPrices?.discountedStartPrice;

  const renderPrice = () => {
    if (!startPrice) return null;

    if (discountedStartPrice)
      return (
        <div className="rounded-bl-xl rounded-tr-xl bg-black/70 p-3">
          <p className="text-micro ">{t('component:card.one_adult_one_night')}</p>
          <div className="flex items-baseline line-through">
            <p data-testid={testIds.with_campaign_cheapest_price} className="mr-1 text-micro">
              {displayInYen(Number(startPrice), router.locale)} ~
            </p>
          </div>

          <div className="flex items-baseline">
            <p
              data-testid={testIds.with_campaign_discount_amount}
              className="mr-1 text-medium font-bold text-white"
            >
              {displayInYen(discountedStartPrice, router.locale)}
            </p>
            <p className="text-mini">~</p>
          </div>
        </div>
      );

    return (
      <div className="rounded-bl-xl rounded-tr-xl bg-black/70 p-3">
        <p className="text-micro">{t('component:card.one_adult_one_night')}</p>
        <div className="flex h-6 items-baseline">
          {isStartPriceLoading ? (
            <Lottie
              style={{ width: '100%', height: '100%' }}
              params={{
                loop: true,
                animationData: Loading,
              }}
            />
          ) : (
            <>
              <p
                data-testid={testIds.no_campaign_cheapest_price}
                className="mr-1 text-medium font-bold text-white"
              >
                {displayInYen(Number(startPrice), router.locale)}
              </p>
              <p className="text-mini">~</p>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-48">
      <ImageWithFallback
        alt={roomPhotoAltText || ''}
        className="relative rounded-xl"
        src={roomPhotoUrl || '/fallback-img-3704x2304.png'}
        url={url}
        layout="fill"
        objectFit="cover"
        sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 320px"
        fallbackSrc="/fallback-img-3704x2304.png"
        onClick={onClick}
        loading={prefetchImage ? 'eager' : 'lazy'}
      >
        <>
          <div className="absolute right-3 top-3 flex flex-col items-end">
            {campaignsForTags.map(
              (x) =>
                x.image?.url && (
                  <div key={`${x.discount}_${x.image.url}`}>
                    <ImageWithFallback
                      data-testid={testIds.campaign_img}
                      src={x.image.url}
                      alt={x.image.alt || ''}
                      fallbackSrc="/fallback-img-3704x2304.png"
                      layout="fixed"
                      objectFit="cover"
                      width={x.image.width}
                      height={x.image.height}
                    />
                  </div>
                )
            )}
          </div>

          <div className="absolute flex h-full items-end text-white">{renderPrice()}</div>
        </>
      </ImageWithFallback>
      <div className="pt-4">
        <h3 className="pb-2 text-regular font-bold text-black">{name || ''}</h3>
        {process.env.NEXT_PUBLIC_FEATURE_FLAG_HOTEL_RATING_IN_FEATURED_HOTEL === 'true' && (
          <HotelRating hotelRating={hotelRating} />
        )}
        <p className="pt-2 text-mini text-black">{address?.postalAddress || ''}</p>
        <p className="line-clamp-2 text-mini text-gray-600">{accessSimple || ''}</p>
      </div>
    </div>
  );
};

export default Card;
